import React, { CSSProperties } from 'react';
import { tableCountOptions } from 'utils/tableViewWidth';

import styles from './TableCountPicker.module.scss';

/**
 * "How many tables am I looking at?" — a SEGMENTED control, Finder-style.
 *
 * This was a dropdown: a trigger showing the current count, and a menu you had
 * to open to discover the rest. Two problems with that shape. It hid the range
 * — a player could not see that four-up was even possible without clicking —
 * and it made a two-step interaction out of a choice between three or four
 * things, which is exactly the size of choice a segmented control exists for
 * (Jason 2026-08-17, citing Finder's view switcher).
 *
 * Every count the window can honour is now a visible segment, and the active
 * one is filled. Choosing is one click, and the set of segments doubles as the
 * answer to "how many could I have?" — which changes as the window is resized,
 * because the options come from the same tableCountOptions the dropdown used.
 *
 * Dropping the menu also deleted a class of bug: the old one was portalled to
 * document.body specifically because the rows this control lives in scroll
 * horizontally and were clipping it. Nothing overflows any more, so none of
 * that machinery is needed.
 */

type TableCountPickerProps = {
  /** How many tables the player has open — an upper bound on the choice. */
  openTables: number;
  /** Current viewport width in px; the other upper bound. */
  viewportWidth: number;
  /** The count actually on screen right now. */
  value: number;
  onChange: (count: number) => void;
  /** Optional overrides from the surrounding header. */
  style?: CSSProperties;
};

/**
 * One rounded box divided into `count` columns — the layout the segment
 * selects, drawn at icon scale. Two tables is literally a box with a line down
 * the middle.
 *
 * ONE <path>, not a <rect> plus <line>s. The colour these render in carries
 * alpha (the resting segment is bone at 55%), and separate elements COMPOSITE:
 * where a divider crossed the box outline the two translucent strokes stacked
 * and the intersections showed as darker knots (Jason 2026-08-17). A single
 * path's stroke is rasterised as one region and painted once, so it holds one
 * uniform opacity through every self-intersection — the SVG-native equivalent
 * of merging the vectors in a drawing tool.
 *
 * `currentColor` throughout, so active, hover and rest are colour changes on
 * one asset rather than a sprite per state.
 */
export const TableCountIcon = ({ count, size = 20 }: { count: number; size?: number }) => {
  // The rounded box as raw path commands (x 3-21, y 5-19, corner radius 2.5),
  // then every interior divider appended as a subpath of the SAME element.
  const dividers = Array.from(
    { length: Math.max(0, count - 1) },
    (_, i) => 3 + ((i + 1) * 18) / count
  );
  const box =
    'M5.5 5 h13 a2.5 2.5 0 0 1 2.5 2.5 v9 a2.5 2.5 0 0 1 -2.5 2.5 h-13 ' +
    'a2.5 2.5 0 0 1 -2.5 -2.5 v-9 a2.5 2.5 0 0 1 2.5 -2.5 Z';
  const d = box + dividers.map((x) => ` M${x} 5 V19`).join('');
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={1.5}
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden
      focusable='false'>
      <path d={d} />
    </svg>
  );
};

const label = (count: number) => (count === 1 ? '1 table' : `${count} tables`);

const TableCountPicker = ({
  openTables,
  viewportWidth,
  value,
  onChange,
  style
}: TableCountPickerProps) => {
  const options = tableCountOptions(viewportWidth, openTables);

  // Nothing to choose between — one felt is the only layout that fits, or the
  // only one the player has tables for. A one-segment control is not a control.
  if (options.length < 2) return null;

  return (
    // radiogroup, not a listbox: these are mutually exclusive settings shown all
    // at once, which is what a radio group IS. Arrow keys move between segments
    // for free, and screen readers announce "2 of 4" rather than reading it as a
    // collapsed menu.
    <div
      className={styles.root}
      role='radiogroup'
      aria-label='Tables in view'
      style={style}
      data-testid='table-count-control'
      data-value={value}>
      {options.map((count) => {
        const active = count === value;
        return (
          <button
            key={count}
            type='button'
            role='radio'
            aria-checked={active}
            aria-label={label(count)}
            title={label(count)}
            onClick={() => onChange(count)}
            className={`${styles.segment} ${active ? styles.segmentActive : ''}`}
            data-testid={`table-count-option-${count}`}
            data-active={active}>
            <TableCountIcon count={count} />
          </button>
        );
      })}
    </div>
  );
};

export default TableCountPicker;
